import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { Box, Button, Typography, IconButton } from "@mui/material";
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import CloseIcon from '@mui/icons-material/Close';

const ToastContext = createContext();
export const useToast = () => useContext(ToastContext);

const SEVERITY = {
    success: { color: '#10B981', Icon: CheckCircleRoundedIcon },
    error:   { color: '#EF4444', Icon: ErrorRoundedIcon },
    warning: { color: '#F59E0B', Icon: WarningAmberRoundedIcon },
    info:    { color: '#427898', Icon: InfoRoundedIcon },
};

let nextId = 0;

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);
    const timers = useRef({});

    const dismiss = useCallback((id) => {
        clearTimeout(timers.current[id]);
        delete timers.current[id];
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    /** `options.action` = { label, onClick } adds a button (e.g. Undo); such toasts stay a little longer. */
    const showToast = useCallback((message, severity = 'info', options = {}) => {
        const id = ++nextId;
        setToasts(prev => [...prev.slice(-2), { id, message, severity, action: options.action }]);
        timers.current[id] = setTimeout(() => dismiss(id), options.action ? 7000 : 4000);
    }, [dismiss]);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 28,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    zIndex: 2000,
                    pointerEvents: 'none',
                }}
            >
                {toasts.map(({ id, message, severity, action }) => {
                    const { color, Icon } = SEVERITY[severity] || SEVERITY.info;
                    return (
                        <Box
                            key={id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.25,
                                pl: 1.5,
                                pr: 1,
                                py: 1.125,
                                bgcolor: '#fff',
                                borderRadius: '10px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.09), 0 1px 4px rgba(0,0,0,0.05)',
                                borderLeft: `3px solid ${color}`,
                                minWidth: 240,
                                maxWidth: 400,
                                pointerEvents: 'all',
                                '@keyframes toastIn': {
                                    from: { opacity: 0, transform: 'translateY(10px) scale(0.98)' },
                                    to:   { opacity: 1, transform: 'translateY(0) scale(1)' },
                                },
                                animation: 'toastIn 0.18s ease-out forwards',
                            }}
                        >
                            <Icon sx={{ fontSize: 16, color, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: 13, color: '#1E293B', fontWeight: 500, flex: 1, lineHeight: 1.45 }}>
                                {message}
                            </Typography>
                            {action && (
                                <Button size="small" onClick={() => { dismiss(id); action.onClick(); }}
                                        sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 12.5, fontWeight: 700, color: '#427898' }}>
                                    {action.label}
                                </Button>
                            )}
                            <IconButton
                                size="small"
                                onClick={() => dismiss(id)}
                                sx={{ p: 0.375, ml: 0.5, color: '#CBD5E1', flexShrink: 0, '&:hover': { color: '#94A3B8', bgcolor: 'transparent' } }}
                            >
                                <CloseIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                        </Box>
                    );
                })}
            </Box>
        </ToastContext.Provider>
    );
};
